import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FlikLogo from '../../components/Logo/FlikLogo';
import RouteMap from '../../components/RouteMap/RouteMap';
import './HomePage.css';

/**
 * Private landing page (route "/"), reached right after login.
 *
 * It renders the FLIK route explorer: a left rail with search, filters and a
 * route list, and an OpenStreetMap surface centred on the user's location with
 * the featured route surfaced as a bottom card.
 */

const FILTERS = [
  { id: 'todo', label: 'Todo' },
  { id: 'senderismo', label: 'Senderismo', type: 'Senderismo' },
  { id: 'barrancos', label: 'Barrancos', type: 'Barranco' },
  { id: 'ferratas', label: 'Vías ferratas', type: 'Vía ferrata' },
];

const ROUTES = [
  {
    id: 'fraile',
    name: 'Barranco del Fraile',
    place: 'Chelva · Valencia',
    type: 'Barranco',
    mide: '3-2-3-4',
    km: '7,4',
    gain: '540',
    time: '4:20',
    featured: true,
  },
  {
    id: 'penyagolosa',
    name: 'Penyagolosa · cara N',
    place: 'Vistabella · Castellón',
    type: 'Senderismo',
    mide: '4-3-3-4',
    km: '12,2',
    gain: '1.130',
    time: '6:10',
  },
  {
    id: 'calderona',
    name: 'Serra Calderona GR-10',
    place: 'Nàquera · Valencia',
    type: 'Senderismo',
    mide: '2-3-3-3',
    km: '11,0',
    gain: '820',
    time: '5:05',
  },
];

const NAV_ITEMS = [
  { id: 'mapa', label: 'Mapa', icon: MapIcon },
  { id: 'favoritos', label: 'Favoritos', icon: StarIcon },
  { id: 'perfil', label: 'Perfil', icon: UserIcon },
];

function HomePage() {
  const premium = process.env.REACT_APP_PREMIUM_ENABLED === 'true';
  const navigate = useNavigate();
  const mapApiRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState('todo');
  const [selectedId, setSelectedId] = useState('fraile');
  const [favourites, setFavourites] = useState({ fraile: true });
  const [activeNav, setActiveNav] = useState('mapa');

  const handleNav = (id) => {
    if (id === 'perfil') {
      navigate('/perfil');
      return;
    }
    setActiveNav(id);
  };

  const filteredRoutes = useMemo(() => {
    const f = FILTERS.find((x) => x.id === activeFilter);
    if (!f || !f.type) return ROUTES;
    return ROUTES.filter((r) => r.type === f.type);
  }, [activeFilter]);

  const selected =
    ROUTES.find((r) => r.id === selectedId) || filteredRoutes[0] || ROUTES[0];

  const toggleFavourite = (id) =>
    setFavourites((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="Home">
      {/* ---- Left rail ---- */}
      <aside className="Home__sidebar">
        <header className="Home__brand">
          <FlikLogo height={20} />
          <button
            type="button"
            className="Home__avatar"
            onClick={() => navigate('/perfil')}
            title="Tu perfil"
            aria-label="Tu perfil"
          >
            <UserIcon />
          </button>
        </header>

        <div className="Home__search">
          <PinIcon />
          <input
            type="search"
            placeholder="Buscar ruta, clima o barranco"
            aria-label="Buscar ruta, clima o barranco"
          />
        </div>

        <div className="Home__filters" role="group" aria-label="Filtros">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`Home__chip${activeFilter === f.id ? ' is-active' : ''}`}
              aria-pressed={activeFilter === f.id}
              onClick={() => setActiveFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <p className="Home__count">{filteredRoutes.length} rutas en esta zona</p>

        <ul className="Home__routes">
          {filteredRoutes.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                className={`RouteItem${selectedId === r.id ? ' is-active' : ''}`}
                onClick={() => setSelectedId(r.id)}
              >
                <div className="RouteItem__head">
                  <span className="RouteItem__name">{r.name}</span>
                  <span
                    className={`RouteItem__star${favourites[r.id] ? ' is-on' : ''}`}
                    aria-hidden="true"
                  >
                    <StarIcon filled={favourites[r.id]} />
                  </span>
                </div>
                <div className="RouteItem__meta">
                  MIDE {r.mide} · {r.km} km · +{r.gain} m
                </div>
                {selectedId === r.id && (
                  <div className="RouteItem__tags">
                    <span className="RouteItem__tag">{r.type.toUpperCase()}</span>
                    <span className="RouteItem__tag RouteItem__tag--soft">{r.time} h</span>
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
        {premium && (<div className="Home__premium">
          <span className="Home__premiumTitle">Hazte Premium</span>
          <span className="Home__premiumText">
            Mapas offline, GPX ilimitados y retos exclusivos.
          </span>
        </div>)
          
        }
        
      </aside>

      {/* ---- Map ---- */}
      <section className="Home__map">
        <RouteMap ref={mapApiRef} />

        {/* Mobile-only floating controls: search + filters over the map.
            Hidden on desktop, where the sidebar owns them. */}
        <div className="Home__mapTop">
          <span className="Home__mapEyebrow">Mapa</span>
          <div className="Home__mapSearchRow">
            <div className="Home__search Home__search--float">
              <PinIcon />
              <input
                type="search"
                placeholder="Buscar ruta o cima"
                aria-label="Buscar ruta o cima"
              />
            </div>
            <button
              type="button"
              className="Home__mapType"
              onClick={() => mapApiRef.current && mapApiRef.current.cycleLayer()}
              aria-label="Cambiar tipo de mapa"
            >
              <LayersIcon />
            </button>
          </div>
          <div
            className="Home__filters Home__filters--float"
            role="group"
            aria-label="Filtros"
          >
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`Home__chip Home__chip--float${
                  activeFilter === f.id ? ' is-active' : ''
                }`}
                aria-pressed={activeFilter === f.id}
                onClick={() => setActiveFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <article className="RouteCard">
          <div className="RouteCard__thumb" aria-hidden="true">
            <span className="RouteCard__badge">Destacada</span>
          </div>
          <div className="RouteCard__body">
            <div className="RouteCard__titleRow">
              <div>
                <h2 className="RouteCard__title">{selected.name}</h2>
                <p className="RouteCard__place">
                  <span className="RouteCard__placeFull">
                    {selected.place.toUpperCase()}
                  </span>
                  <span className="RouteCard__placeMide">
                    {selected.place.split('·')[0].trim().toUpperCase()} · MIDE{' '}
                    {selected.mide}
                  </span>
                </p>
              </div>
              <div className="RouteCard__actions">
                <button
                  type="button"
                  className={`RouteCard__icon${favourites[selected.id] ? ' is-on' : ''}`}
                  onClick={() => toggleFavourite(selected.id)}
                  aria-label="Guardar en favoritos"
                >
                  <StarIcon filled={favourites[selected.id]} />
                </button>
                <button type="button" className="RouteCard__icon" aria-label="Compartir">
                  <ShareIcon />
                </button>
              </div>
            </div>

            <dl className="RouteCard__stats">
              <div>
                <dt>{selected.km}</dt>
                <dd>km</dd>
              </div>
              <div>
                <dt>{selected.gain}</dt>
                <dd>D+</dd>
              </div>
              <div>
                <dt>{selected.time}</dt>
                <dd>horas</dd>
              </div>
              <div>
                <dt>{selected.mide}</dt>
                <dd>MIDE</dd>
              </div>
            </dl>

            <div className="RouteCard__cta">
              <button type="button" className="RouteCard__primary">
                Ver ruta
                <span className="RouteCard__arrow" aria-hidden="true">
                  →
                </span>
              </button>
              <button type="button" className="RouteCard__secondary">
                Descargar GPX
              </button>
            </div>
          </div>
        </article>
      </section>

      {/* ---- Mobile bottom nav ---- */}
      <nav className="Home__nav" aria-label="Navegación principal">
        {NAV_ITEMS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            className={`Home__navItem${activeNav === id ? ' is-active' : ''}`}
            aria-current={activeNav === id ? 'page' : undefined}
            onClick={() => handleNav(id)}
          >
            <Icon />
            <span>{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

/* ---- Inline icons (no asset dependencies) ---- */

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
      <path
        d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <circle cx="12" cy="10" r="2.4" fill="currentColor" />
    </svg>
  );
}

function StarIcon({ filled = false }) {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="m12 3 2.6 5.6 6.1.7-4.5 4.2 1.2 6-5.4-3-5.4 3 1.2-6L3.3 9.3l6.1-.7L12 3Z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        d="M4 12v7a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-7M12 3v13M8 7l4-4 4 4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MapIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path
        d="m9 4-6 2v14l6-2 6 2 6-2V4l-6 2-6-2Zm0 0v14m6-12v14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LayersIcon() {
  return (
    <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
      <path
        d="M12 3 3 8l9 5 9-5-9-5Zm-9 8 9 5 9-5m-18 5 9 5 9-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <circle cx="12" cy="8" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default HomePage;
